import React, { useMemo, useState } from 'react';
import { Heart } from 'lucide-react';
import FlagIcon from './FlagIcon';
import LanguagePreviewDropdown from './LanguagePreviewDropdown';
import LanguageListPopover from './LanguageListPopover';
import DarkTooltip from '../ui/DarkTooltip';
import {
  getFlagCode,
  getPrimaryFlagCode,
  getCountryName,
  uniqueLanguages,
  translate,
  GENDER_PT,
  AGE_PT,
  CATEGORY_PT,
  ACCENT_PT,
  DESCRIPTIVE_PT,
  USE_CASE_PT,
  LANGUAGE_PT,
} from '../../utils/voiceMetadata';

/**
 * VoiceCardExpanded — card grande exibido na grade do VoiceLibraryModal.
 *
 * FASE 2.2: dropdown de previews por idioma é INLINE dentro do card (substituiu
 * o botão "Detalhes" que abria modal sobreposto). VoiceDetailsModal foi removido.
 *
 * Layout (mais compacto, inspirado no ElevenLabs):
 *   [Avatar 36]  Nome [MULTIVOZ?]                       [♡]
 *                tom · uso
 *   "Descrição em inglês" (2 linhas máx)
 *   [tag categoria] [tag sotaque] [tag idade] [tag uso]   (1 linha, scroll horizontal se overflow)
 *   🇺🇸 +N idiomas extras
 *   [▼ Pré-visualização de Idioma]   (se tiver verified_languages)
 *   [▶ Preview]                                  [Usar]
 */
export default function VoiceCardExpanded({
  voice,
  isFavorite = false,
  onToggleFavorite,
  onSelectVoice,
  onPlayLanguage,        // (voice, language, accent, url, key) — opcional
  currentLanguageKey,    // string|null
  avatar,
}) {
  const info = voice.voice_info;
  const isCapCut = (voice.provider || '').toLowerCase() === 'capcut';
  const [hover, setHover] = useState(false);
  // FIX 4: popover de idiomas (clicado no "+N" rótulo) renderizado em portal
  const [popoverAnchor, setPopoverAnchor] = useState(null);

  const flagData = useMemo(() => {
    if (!info) return null;
    const code = getPrimaryFlagCode(voice);
    const unique = uniqueLanguages(info.verified_languages);
    const others = unique.filter((e) => {
      const l = (e.language || '').toLowerCase();
      const a = (e.accent || '').toLowerCase();
      return l !== (info.language || '').toLowerCase() || a !== (info.accent || '').toLowerCase();
    });
    return { code, others };
  }, [info, voice]);

  const hasVerifiedLanguages = !!(info && Array.isArray(info.verified_languages) && info.verified_languages.length > 0);

  // Gênero: SÓ voice.gender top-level (Carlos). Evita "male" sujo do voice_info ElevenLabs.
  const genderPt = voice.gender ? translate(voice.gender, GENDER_PT) : '';
  const descriptivePt = info && info.descriptive ? translate(info.descriptive, DESCRIPTIVE_PT) : '';
  const useCasePt = info && info.use_case ? translate(info.use_case, USE_CASE_PT) : '';

  // FIX 3: rich tooltip do nome (mesmo padrão do VoiceCardCompact).
  // Forçado position="top" pra evitar abrir lateral em cards próximos à borda
  // direita do modal Library.
  const richTooltipContent = useMemo(() => {
    const langPt = info && info.language ? translate(info.language, LANGUAGE_PT) : '';
    const agePt = info && info.age ? translate(info.age, AGE_PT) : '';
    const description = info && info.description ? String(info.description) : '';
    const tags = [];
    if (info && info.category) tags.push(translate(info.category, CATEGORY_PT));
    if (info && info.accent && info.accent.toLowerCase() !== 'standard') tags.push(translate(info.accent, ACCENT_PT));
    if (info && info.use_case) tags.push(translate(info.use_case, USE_CASE_PT));
    const tagsTrimmed = tags.filter(Boolean).slice(0, 3);
    const primaryFlagCode = flagData ? flagData.code : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--dp-text-primary)' }}>
          {voice.name}
        </div>
        <div style={{
          display: 'flex',
          gap: 6,
          fontSize: 11,
          color: 'var(--dp-text-secondary)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          {primaryFlagCode && <FlagIcon code={primaryFlagCode} size="14px" rounded />}
          {langPt && <span>{langPt}</span>}
          {genderPt && <><span style={{ opacity: 0.4 }}>·</span><span>{genderPt}</span></>}
          {agePt && <><span style={{ opacity: 0.4 }}>·</span><span>{agePt}</span></>}
        </div>
        {tagsTrimmed.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
            {tagsTrimmed.map((tag, i) => (
              <span key={i} style={{
                fontSize: 10,
                padding: '2px 6px',
                borderRadius: 4,
                background: 'rgba(13, 174, 255, 0.10)',
                color: '#06B6D4',
                whiteSpace: 'nowrap',
              }}>{tag}</span>
            ))}
          </div>
        )}
        {description && (
          <div style={{
            fontSize: 11,
            color: 'var(--dp-text-tertiary)',
            lineHeight: 1.4,
            marginTop: 4,
            fontStyle: 'italic',
          }}>
            “{description.length > 120 ? description.substring(0, 120) + '…' : description}”
          </div>
        )}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice, info, flagData, genderPt]);

  // Adapter: dropdown chama com (lang, accent, url, key); consumer espera (voice, ...)
  const handleDropdownPlay = (language, accent, previewUrl, key) => {
    if (onPlayLanguage) onPlayLanguage(voice, language, accent, previewUrl, key);
  };

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'var(--dp-bg-card)',
        border: `1px solid ${hover ? 'rgba(13,174,255,0.30)' : 'var(--dp-border-subtle)'}`,
        borderRadius: '12px',
        padding: '12px 12px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        transition: 'all 0.25s ease',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover ? '0 8px 24px rgba(0,0,0,0.25), 0 0 0 1px rgba(13,174,255,0.12)' : '0 1px 2px rgba(0,0,0,0.08)',
      }}
    >
      {/* Header: avatar + nome + favorito */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        {avatar}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--dp-text-primary)',
            lineHeight: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            flexWrap: 'wrap',
          }}>
            <DarkTooltip content={richTooltipContent} delay={900} position="top" maxWidth={320}>
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                maxWidth: '100%',
              }}>{voice.name}</span>
            </DarkTooltip>
            {voice.name === 'Multivoz' && (
              <span style={{
                fontSize: '8.5px',
                fontWeight: 700,
                color: 'var(--dp-blue-500)',
                background: 'rgba(13,174,255,0.14)',
                border: '1px solid rgba(13,174,255,0.35)',
                padding: '1px 5px',
                borderRadius: '9999px',
                letterSpacing: '0.4px',
              }}>MULTIVOZ</span>
            )}
            {isCapCut && (
              <span style={{
                fontSize: '8.5px',
                fontWeight: 700,
                color: '#F59E0B',
                background: 'rgba(245,158,11,0.10)',
                border: '1px solid rgba(245,158,11,0.30)',
                padding: '1px 5px',
                borderRadius: '4px',
                letterSpacing: '0.4px',
              }}>CapCut</span>
            )}
          </div>
          <div style={{
            marginTop: '1px',
            fontSize: '11px',
            color: 'var(--dp-text-tertiary)',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            flexWrap: 'wrap',
            lineHeight: 1.3,
          }}>
            {genderPt && <span>{genderPt}</span>}
            {descriptivePt && <><span style={{ opacity: 0.4 }}>·</span><span>{descriptivePt}</span></>}
            {useCasePt && <><span style={{ opacity: 0.4 }}>·</span><span>{useCasePt}</span></>}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleFavorite && onToggleFavorite(voice); }}
          title={isFavorite ? 'Remover dos favoritos' : 'Favoritar'}
          style={{
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            border: '1px solid var(--dp-border-subtle)',
            background: isFavorite ? 'rgba(248,113,113,0.10)' : 'transparent',
            color: isFavorite ? '#F87171' : 'var(--dp-text-tertiary)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.2s',
          }}
        >
          <Heart size={12} fill={isFavorite ? '#F87171' : 'none'} />
        </button>
      </div>

      {/* Descrição (2 linhas máx) */}
      {info && info.description && (
        <p style={{
          margin: 0,
          fontSize: '11px',
          lineHeight: 1.4,
          color: 'var(--dp-text-secondary)',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          fontStyle: 'italic',
          opacity: 0.85,
        }}>
          “{info.description}”
        </p>
      )}

      {/* Tags em 1 linha (overflow horizontal escondido — o necessário cabe) */}
      {info && (
        <div style={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: '4px',
          overflow: 'hidden',
        }}>
          {info.category && <Tag color="#06B6D4" label={translate(info.category, CATEGORY_PT)} />}
          {info.accent && <Tag color="#8B5CF6" label={translate(info.accent, ACCENT_PT)} />}
          {info.age && <Tag color="#F59E0B" label={translate(info.age, AGE_PT)} />}
          {info.use_case && <Tag color="#3B82F6" label={translate(info.use_case, USE_CASE_PT)} />}
        </div>
      )}

      {/* Bandeiras adicionais (idiomas além do primário) */}
      {flagData && flagData.others.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          fontSize: '10px',
          color: 'var(--dp-text-tertiary)',
        }}>
          <DarkTooltip content={getCountryName(flagData.code)} delay={300}>
            <FlagIcon code={flagData.code} size="13px" rounded />
          </DarkTooltip>
          <span style={{ display: 'inline-flex', gap: '2px', alignItems: 'center', flexShrink: 0 }}>
            {flagData.others.slice(0, 5).map((e, i) => {
              const code = getFlagCode(e.language, e.accent, e.locale);
              return (
                <DarkTooltip key={`${e.language}|${e.accent || ''}|${i}`} content={getCountryName(code)} delay={300}>
                  <FlagIcon code={code} size="10px" rounded />
                </DarkTooltip>
              );
            })}
            {flagData.others.length > 5 && (
              <DarkTooltip
                content={`${flagData.others.length - 5} ${flagData.others.length - 5 === 1 ? 'idioma' : 'idiomas'} adicional — clique pra ver`}
                delay={300}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    setPopoverAnchor(rect);
                  }}
                  style={{
                    fontSize: '9px',
                    fontWeight: 600,
                    color: 'var(--dp-blue-500)',
                    background: 'rgba(13,174,255,0.10)',
                    padding: '0 4px',
                    borderRadius: '6px',
                    lineHeight: 1.4,
                    border: '1px solid transparent',
                    cursor: 'pointer',
                    fontFamily: 'var(--dp-font)',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.18)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.10)'; }}
                >+{flagData.others.length - 5}</button>
              </DarkTooltip>
            )}
          </span>
          <span style={{ opacity: 0.6 }}>· {flagData.others.length + 1} {flagData.others.length === 0 ? 'idioma' : 'idiomas'}</span>
        </div>
      )}

      {/* ITER FINAL: Dropdown (label completo) + Usar.
          Botão "Preview" removido — preview já é acessível via click no avatar
          ou via dropdown de idioma (que toca direto ao selecionar entry). */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: 'auto',
        paddingTop: '2px',
        minWidth: 0,
      }}>
        {hasVerifiedLanguages && (
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
            <LanguagePreviewDropdown
              voice={voice}
              inline={true}
              compactLabel={false}
              currentLanguageKey={currentLanguageKey}
              onPlayLanguage={handleDropdownPlay}
            />
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onSelectVoice && onSelectVoice(voice); }}
          style={{
            flexShrink: 0,
            padding: '6px 16px',
            borderRadius: '8px',
            border: 'none',
            background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
            color: 'white',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            fontFamily: 'var(--dp-font)',
            transition: 'all 0.2s',
            boxShadow: '0 2px 8px rgba(59,130,246,0.25)',
            whiteSpace: 'nowrap',
          }}
        >
          Usar
        </button>
      </div>

      {/* FIX 4: popover de idiomas (renderizado em portal pelo próprio componente) */}
      {popoverAnchor && (
        <LanguageListPopover
          voice={voice}
          anchorRect={popoverAnchor}
          onClose={() => setPopoverAnchor(null)}
        />
      )}
    </div>
  );
}

// ─── Sub-helpers ──────────────────────────────────────────────────────────

function Tag({ color, label }) {
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 600,
        color,
        background: `${color}1A`,         // 1A ≈ 10% alpha em hex
        border: `1px solid ${color}40`,    // 40 ≈ 25% alpha em hex
        padding: '2px 7px',
        borderRadius: '9999px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flexShrink: 1,
        minWidth: 0,
      }}
    >
      {label}
    </span>
  );
}
